import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Wand2, Check, X, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API_BASE } from "../lib/api";
import { Button } from "./ui/button";

const CHANGE_LABELS = {
  waypoints: "Tussenstops",
  avoid_places: "Vermijden",
  planned_stays: "Overnachtingen (heen)",
  planned_stays_return: "Overnachtingen (terug)",
  route_preference: "Routevoorkeur",
  max_km_per_day: "Max km per dag",
  nights_to_destination: "Nachten onderweg",
  add_charging_stops: "Laadstops toevoegen",
  replace_charging_stops: "Laadstop vervangen",
  remove_charging_stops: "Laadstop verwijderen",
  add_extra_nights: "Extra nacht(en)",
  split_days: "Rijdag splitsen",
  round_trip: "Rondrit",
  avoid_toll: "Tolwegen vermijden",
  avoid_ferry: "Veerboten vermijden",
  start_date: "Vertrekdatum",
  end_date: "Einddatum",
  origin_query: "Vertrekpunt",
  destination_query: "Bestemming",
  destination_stay_name: "Verblijf op bestemming",
};

const formatValue = (key, val) => {
  if (val === null || val === undefined || (Array.isArray(val) && val.length === 0)) return "—";
  if (typeof val === "boolean") return val ? "ja" : "nee";
  if (key === "planned_stays" || key === "planned_stays_return") {
    return val.map((p) => `${p.location}${p.name ? ` (${p.name})` : ""} · ${p.nights}n`).join(" → ");
  }
  if (key === "add_charging_stops") {
    return val.map((s) => `dag ${s.day_number}: ${s.query}`).join(" · ");
  }
  if (key === "replace_charging_stops") {
    return val.map((s) => `dag ${s.day_number}: ${s.old_name} → ander station (${s.query})`).join(" · ");
  }
  if (key === "remove_charging_stops") {
    return val.map((s) => `dag ${s.day_number}: ${s.name}`).join(" · ");
  }
  if (key === "add_extra_nights") {
    return val.map((s) => `dag ${s.day_number}${(s.nights || 1) > 1 ? ` (${s.nights} nachten)` : ""}`).join(" · ");
  }
  if (key === "split_days") {
    return val.map((s) => `dag ${s.day_number}${s.km ? ` — overnachting na ~${Math.round(s.km)} km` : " (halverwege)"}`).join(" · ");
  }
  if (Array.isArray(val)) return val.join(" · ");
  return String(val);
};

const deltaTxt = (n) => (n === 0 ? "±0" : n > 0 ? `+${n}` : `−${Math.abs(n)}`);
const deltaColor = (n) => (n === 0 ? "text-muted-foreground" : n > 0 ? "text-amber-700" : "text-[#2D6A4F]");

export const ImpactChip = ({ icon: Icon, label, value, delta, testId }) => (
  <span data-testid={testId} className="inline-flex items-center gap-1 rounded-full bg-white border border-[#3A6EA5]/30 px-2.5 py-1 text-[11px] font-bold">
    <Icon className="w-3 h-3 text-[#3A6EA5]" />
    {value != null && <span>{value} {label}</span>}
    <span className={deltaColor(delta)}>({deltaTxt(delta)}{value == null ? ` ${label}` : ""})</span>
  </span>
);

const ProposalCard = ({ changes, tripId, canApply, onApplied, entryKey, messageId, applied, onSetApplied }) => {
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [impact, setImpact] = useState(null);
  if (dismissed) return null;

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${tripId}/assistant/apply`, { changes, message_id: messageId || null });
      if (data.regenerating) {
        toast.success("Wijzigingen toegepast — je planning wordt opnieuw berekend!");
      } else {
        const parts = [];
        if (data.added_stops?.length) parts.push(`Laadstop toegevoegd: ${data.added_stops.join(", ")}`);
        if (data.replaced_stops?.length) parts.push(`Laadstop vervangen: ${data.replaced_stops.join(", ")}`);
        if (data.removed_stops?.length) parts.push(`Laadstop verwijderd: ${data.removed_stops.join(", ")}`);
        toast.success(parts.join(" · ") || "Wijzigingen toegepast!");
        const stopDelta = (data.added_stops?.length || 0) - (data.removed_stops?.length || 0);
        setImpact({ stopsOnly: true, delta: { charge_stops: stopDelta } });
      }
      onSetApplied(entryKey);
      onApplied?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="assistant-proposal-card" className="mt-2 rounded-2xl border border-[#3A6EA5]/30 bg-[#3A6EA5]/5 p-3.5 max-w-[92%]">
      <p className="text-xs font-bold flex items-center gap-1.5 text-[#3A6EA5] uppercase tracking-wide mb-2">
        <Wand2 className="w-3.5 h-3.5" /> Voorgestelde planwijziging
      </p>
      <div className="space-y-1 mb-3">
        {Object.entries(changes).map(([k, v]) => (
          <p key={k} className="text-xs leading-relaxed" data-testid={`proposal-change-${k}`}>
            <span className="font-semibold">{CHANGE_LABELS[k] || k}:</span> {formatValue(k, v)}
          </p>
        ))}
      </div>
      {applied ? (
        <div>
          <p data-testid="proposal-applied-label" className="text-xs font-semibold text-primary flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Toegepast</p>
          {impact?.stopsOnly && (
            <div data-testid="proposal-impact" className="mt-2 flex flex-wrap gap-1.5">
              <ImpactChip icon={Zap} label={Math.abs(impact.delta.charge_stops) === 1 ? "laadstop" : "laadstops"} delta={impact.delta.charge_stops} testId="impact-stops" />
            </div>
          )}
        </div>
      ) : canApply ? (
        <div className="flex gap-2">
          <button data-testid="proposal-apply-button" onClick={apply} disabled={busy}
            className="rounded-full bg-[#3A6EA5] hover:bg-[#2d5784] text-white text-xs font-bold px-4 py-2 flex items-center gap-1.5 disabled:opacity-50 active:scale-95 transition-[transform,background-color] duration-200">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Pas toe op planning
          </button>
          <button data-testid="proposal-dismiss-button" onClick={() => setDismissed(true)}
            className="rounded-full bg-secondary hover:bg-border text-xs font-semibold px-4 py-2 flex items-center gap-1.5 active:scale-95 transition-[transform,background-color] duration-200">
            <X className="w-3.5 h-3.5" /> Negeer
          </button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground italic">Alleen de eigenaar van de trip kan wijzigingen toepassen.</p>
      )}
    </div>
  );
};

export const AssistantChat = ({ tripId, canApply = true, onApplied, liveDay = null }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [appliedKeys, setAppliedKeys] = useState({});
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get(`/trips/${tripId}/chat`).then((r) => setMessages(r.data)).catch(() => {});
  }, [tripId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const failLast = (msg) => {
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { role: "assistant", content: msg };
      return copy;
    });
  };

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);

    const controller = new AbortController();
    let idleTimer = setTimeout(() => controller.abort(), 75000);
    const resetIdle = () => { clearTimeout(idleTimer); idleTimer = setTimeout(() => controller.abort(), 75000); };
    let gotContent = false;
    try {
      const res = await fetch(`${API_BASE}/trips/${tripId}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: text, live_day: liveDay }),
        signal: controller.signal,
      });
      if (!res.ok) {
        let detail = "";
        try { detail = formatApiError((await res.json()).detail); } catch { /* geen json-body */ }
        failLast(res.status === 401
          ? "Je sessie is verlopen — herlaad de pagina en log opnieuw in, dan kan ik verder helpen."
          : detail || "De assistent is even niet bereikbaar. Probeer het zo opnieuw.");
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        resetIdle();
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const evt = JSON.parse(part.slice(6));
          if (evt.delta) {
            gotContent = true;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + evt.delta };
              return copy;
            });
          } else if (evt.changes) {
            gotContent = true;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], changes: evt.changes, id: evt.message_id || copy[copy.length - 1].id };
              return copy;
            });
          } else if (evt.done) {
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, id: evt.message_id || last.id, content: last.content.replace(/\[VOLTROUTE_CHANGES\][\s\S]*?(\[\/VOLTROUTE_CHANGES\]|$)/, "").trim() };
              return copy;
            });
          } else if (evt.error) {
            gotContent = true;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: `Sorry, er ging iets mis: ${evt.error}` };
              return copy;
            });
          }
        }
      }
      if (!gotContent) failLast("Er kwam geen antwoord binnen. Probeer het zo opnieuw.");
    } catch (e) {
      if (!gotContent) {
        failLast(controller.signal.aborted
          ? "De assistent deed er te lang over. Probeer het opnieuw."
          : "Verbinding onderbroken. Probeer het opnieuw.");
      } else {
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, content: `${last.content}\n\n⚠️ De verbinding werd onderbroken — het antwoord is mogelijk onvolledig.` };
          return copy;
        });
      }
    } finally {
      clearTimeout(idleTimer);
      setStreaming(false);
    }
  };

  const display = (c) => (c || "").split("[VOLTROUTE_CHANGES]")[0].trimEnd();
  const richText = (t) => t.split(/\*\*(.+?)\*\*/g).map((p, i) => (i % 2 ? <strong key={i}>{p}</strong> : p));

  return (
    <div className="flex flex-col h-[60vh] bg-card rounded-2xl overflow-hidden" data-testid="assistant-chat">
      <div className="p-4 border-b border-border flex items-center gap-2">
        <span className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-primary-foreground" />
        </span>
        <div>
          <p className="font-heading font-bold text-sm">AI Reisassistent</p>
          <p className="text-xs text-muted-foreground">Vraag om tips — of om je route echt aan te passen</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-sm text-muted-foreground space-y-2">
            <p>Probeer bijvoorbeeld:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>"Rij via Reims en vermijd Parijs"</li>
              <li>"Voeg een extra nacht toe in Reims"</li>
              <li>"Splits dag 3 met een overnachting onderweg"</li>
              <li>"Waar kan ik goed lunchen nabij de eerste laadstop?"</li>
            </ul>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
              {display(m.content) ? richText(display(m.content)) : (m.changes ? "Voorstel hieronder 👇" : <span className="animate-pulse-soft">Aan het typen...</span>)}
            </div>
            {m.role === "assistant" && m.changes && (
              <ProposalCard changes={m.changes} tripId={tripId} canApply={canApply} onApplied={onApplied}
                entryKey={m.id || `msg-${i}`} messageId={m.id} applied={!!m.applied || !!appliedKeys[m.id || `msg-${i}`]}
                onSetApplied={(k) => setAppliedKeys((a) => ({ ...a, [k]: true }))} />
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="p-3 border-t border-border flex gap-2">
        <input
          data-testid="assistant-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Stel je vraag of vraag een routewijziging..."
          className="flex-1 bg-secondary rounded-full px-5 py-3 text-sm outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200"
        />
        <Button data-testid="assistant-chat-send-button" onClick={send} disabled={streaming || !input.trim()}
          className="rounded-full bg-primary hover:bg-[#1B4332] w-12 h-12 p-0 active:scale-90 transition-[transform,background-color] duration-200">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
